import { EmailTemplateTypes } from "../../../common/constants"
import { DEFAULT_SEND_TIME } from "../../../common/timeOptions"
import { InvoiceReminderSchedule, InvoiceReminderStep, ReminderAnchor, ReminderDayRange } from "../../../domain/invoicing/settings/invoiceReminderSchedule"
import { uniqueId } from "../../../helpers/uniqueId"
import { EmailTemplate } from "../../EmailTemplates/emailTemplateModel"

export const NO_TEMPLATE_GUID = '00000000-0000-0000-0000-000000000000'

export const DUPLICATE_DAY_WARNING = 'Another reminder is already scheduled for this day.'
export const TEMPLATE_REQUIRED_ERROR = 'Choose a template for this reminder.'

export const daysRangeError = (range: ReminderDayRange): string => {
  return `Enter a number of days between ${range.minDays} and ${range.maxDays}.`
}

export const anchorOptions: { label: string, value: ReminderAnchor }[] = [
  { label: 'before', value: 'before' },
  { label: 'on', value: 'on' },
  { label: 'after', value: 'after' },
]

export type InvoiceReminderStepView = InvoiceReminderStep & {
  showDays: boolean
  daysError?: string
  daysWarning?: string
  templateError?: string
}

export type InvoiceReminderScheduleView = {
  steps: InvoiceReminderStepView[]
  isEnabled: boolean
  hasErrors: boolean
}

export const dueDateOffset = (step: Pick<InvoiceReminderStep, 'anchor' | 'days'>): number => {
  if (step.anchor === 'before') {
    return -step.days
  }
  if (step.anchor === 'after') {
    return step.days
  }
  return 0
}

export const setEnabled = (schedule: InvoiceReminderSchedule, enabled: boolean): InvoiceReminderSchedule => ({
  ...schedule,
  enabled
})

const mapStep = (schedule: InvoiceReminderSchedule, id: string, map: (step: InvoiceReminderStep) => InvoiceReminderStep): InvoiceReminderSchedule => ({
  ...schedule,
  steps: schedule.steps.map(step => step.id === id ? map(step) : step)
})

export const setDays = (schedule: InvoiceReminderSchedule, id: string, days: number): InvoiceReminderSchedule => {
  return mapStep(schedule, id, step => ({ ...step, days }))
}

export const setAnchor = (schedule: InvoiceReminderSchedule, id: string, anchor: ReminderAnchor): InvoiceReminderSchedule => {
  return mapStep(schedule, id, step => {
    if (anchor === 'on') {
      return { ...step, anchor, days: 0 }
    }
    return { ...step, anchor, days: step.days === 0 ? 1 : step.days }
  })
}

export const setAtTime = (schedule: InvoiceReminderSchedule, id: string, atTime: string): InvoiceReminderSchedule => {
  return mapStep(schedule, id, step => ({ ...step, atTime }))
}

export const setEmailTemplateGuid = (schedule: InvoiceReminderSchedule, id: string, emailTemplateGuid: string): InvoiceReminderSchedule => {
  return mapStep(schedule, id, step => ({ ...step, emailTemplateGuid }))
}

export const addReminder = (schedule: InvoiceReminderSchedule, range: ReminderDayRange): InvoiceReminderSchedule => {
  const highestOffset = schedule.steps.length === 0 ? 0 : Math.max(...schedule.steps.map(dueDateOffset))
  const days = Math.min(range.maxDays, Math.max(1, highestOffset + 1))
  const step: InvoiceReminderStep = {
    id: uniqueId(),
    days,
    anchor: 'after',
    atTime: DEFAULT_SEND_TIME,
    emailTemplateGuid: NO_TEMPLATE_GUID
  }
  return { ...schedule, steps: [...schedule.steps, step] }
}

export const removeReminder = (schedule: InvoiceReminderSchedule, id: string): InvoiceReminderSchedule => {
  return { ...schedule, steps: schedule.steps.filter(step => step.id !== id) }
}

export const getInvoiceReminderTemplates = (templates: EmailTemplate[]): EmailTemplate[] => {
  return templates.filter(template => template.type === EmailTemplateTypes.InvoiceReminder)
}

export const getInvoiceReminderTemplateOptions = (templates: EmailTemplate[]): { label: string, value: string }[] => {
  return getInvoiceReminderTemplates(templates).map(template => ({ label: template.name, value: template.guid }))
}

export const findInvoiceReminderTemplate = (templates: EmailTemplate[], guid: string): EmailTemplate | undefined => {
  return getInvoiceReminderTemplates(templates).find(template => template.guid === guid)
}

const stepDaysError = (step: InvoiceReminderStep, range: ReminderDayRange): string | undefined => {
  if (step.days < range.minDays || step.days > range.maxDays) {
    return daysRangeError(range)
  }
  return undefined
}

const stepDaysWarning = (offsets: number[], index: number): string | undefined => {
  if (offsets.filter(offset => offset === offsets[index]).length > 1) {
    return DUPLICATE_DAY_WARNING
  }
  return undefined
}

export const toView = (schedule: InvoiceReminderSchedule, templates: EmailTemplate[], range: ReminderDayRange): InvoiceReminderScheduleView => {
  const offsets = schedule.steps.map(dueDateOffset)
  const steps = schedule.steps.map((step, index) => ({
    ...step,
    showDays: step.anchor !== 'on',
    daysError: stepDaysError(step, range),
    daysWarning: stepDaysWarning(offsets, index),
    templateError: findInvoiceReminderTemplate(templates, step.emailTemplateGuid) === undefined ? TEMPLATE_REQUIRED_ERROR : undefined
  }))

  return {
    steps,
    isEnabled: schedule.enabled,
    hasErrors: steps.some(step => step.daysError !== undefined || step.templateError !== undefined)
  }
}
